import { Table } from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import type { ConversationAndRequester } from 'lib-shared-types-api';
import { MouseEventHandler, useCallback } from 'react';

import { AppUrlOptions, createAppUrl } from 'utils/url/createAppUrl';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useIsRestrictedFromQueue } from 'utils/user/useUserQueues';

import { ConversationsTableActions } from './components/rowCells/actions/conversationsTableActions';
import { ConversationsTableAssignedAgent } from './components/rowCells/assignedAgent/conversationsTableAssignedAgent';
import { ConversationsTableChannel } from './components/rowCells/channel/conversationsTableChannel';
import { ConversationsTableCsid } from './components/rowCells/csid/conversationsTableCsid';
import { ConversationsTableCustomer } from './components/rowCells/customer/conversationsTableCustomer';
import { ConversationsTableQueue } from './components/rowCells/queue/conversationsTableQueue';
import { ConversationsTableStatus } from './components/rowCells/status/conversationsTableStatus';
import { ConversationsTableTimestamp } from './components/rowCells/timestamp/conversationsTableTimestamp';
import styles from './conversationsTableConversationRow.module.scss';
import { ConversationRowContext } from './utils/conversationRowContext';

export type ConversationsTableConversationRowProps = {
  conversationAndRequester: ConversationAndRequester;
};

export const ConversationsTableConversationRow = ({
  conversationAndRequester,
}: ConversationsTableConversationRowProps): JSX.Element => {
  const navigate = useAppNavigate();

  const navigateToConversation = useCallback<MouseEventHandler<HTMLElement>>(
    (e) => {
      const isCtrlOrCmdPressed = e.metaKey || e.ctrlKey;

      const urlOptions: AppUrlOptions<'/conversation/:csid'> = {
        path: '/conversation/:csid',
        params: {
          path: {
            csid: conversationAndRequester.conversation.id.toString(),
          },
        },
      };

      if (isCtrlOrCmdPressed) {
        window.open(createAppUrl(urlOptions), '_blank');
      } else {
        navigate(urlOptions);
      }
    },
    [conversationAndRequester.conversation.id, navigate],
  );

  /** Handles clicking the row. */
  const handleRowClick = useCallback<MouseEventHandler<HTMLElement>>(
    (e) => {
      const { target } = e;

      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLButtonElement ||
        target instanceof SVGElement ||
        // Assumes that the first cell in the row is the implicitly added
        // checkbox cell. If for some reason the checkbox is ever going to be
        // optional then it should be guarded here.
        (target instanceof HTMLTableCellElement &&
          target.parentElement?.childNodes[0] === target) ||
        (target instanceof HTMLTableCellElement &&
          target.parentElement?.childNodes[
            target.parentElement?.childNodes.length - 1
          ] === target)
      ) {
        return;
      }

      navigateToConversation(e);

      ampli.conversationsGoToConversationClicked();
    },
    [navigateToConversation],
  );

  const isRestricted = useIsRestrictedFromQueue(
    conversationAndRequester.conversation.queue,
  );

  return (
    <ConversationRowContext.Provider
      value={{ conversationAndRequester, isRestricted }}
    >
      <Table.Tr
        className={isRestricted ? styles.rootRestricted : styles.root}
        rowId={conversationAndRequester.conversation.id}
        onClick={isRestricted ? undefined : handleRowClick}
      >
        <Table.Td>
          <ConversationsTableCsid />
        </Table.Td>

        <Table.Td>
          <ConversationsTableCustomer />
        </Table.Td>

        <Table.Td>
          <ConversationsTableQueue />
        </Table.Td>

        <Table.Td>
          <ConversationsTableStatus />
        </Table.Td>

        <Table.Td>
          <ConversationsTableChannel />
        </Table.Td>

        <Table.Td>
          <ConversationsTableAssignedAgent />
        </Table.Td>

        <Table.Td>
          <ConversationsTableTimestamp timestampKey="last_message_created_at" />
        </Table.Td>

        <Table.Td>
          <ConversationsTableTimestamp timestampKey="created_at" />
        </Table.Td>

        <Table.Td>
          <ConversationsTableActions />
        </Table.Td>
      </Table.Tr>
    </ConversationRowContext.Provider>
  );
};
